import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MAGIC_HEAVY_KB,
  MODEL_ACCEPT,
  magicHealth,
  servedState,
  sizeKbFromHeaders,
  type CameraState,
  type HealthAssets,
  type MagicHealthRow,
  type PublishState,
  type ServedState,
} from "@greg-chick/shared";
import { adminApi, contentBase, loadBookDoc } from "../lib/contentApi";
import { MagicPublishCard } from "./MagicPublishCard";
import { PageHead } from "./ui";

/**
 * 🩺 **마법 점검** — 책마다 「누르면 일어나는 마법」이 아이 화면에 잘 나가는지 한눈에.
 *
 * ## 왜 (2026-09-13)
 * 운영자가 「25쪽 카메라 끄기를 눌렀는데 됐는지 알 수가 없다」고 했다. 게시됐나·그림이 오나·카메라가
 * 켜졌나는 지금까지 **명령줄 검사(`pnpm check:magic`)만** 알았다 — 명령줄뿐이면 운영자는 못 본다.
 *
 * ## 🔴 판정은 shared(`magicHealth`·`servedState`) — 이 화면은 물어보고 보여 주기만
 * 「게시됨」의 뜻은 [🚀 아이 화면에 올리기](`MagicPublishCard`)와 같은 비교다. 여기서 다시 짜면
 * 「올리기 카드는 다 올라갔다는데 점검은 안 올렸다」가 된다.
 *
 * ## 🔴 운영자 말로
 * 「저장소」·「게시본」은 개발자 말이다. 「아이 화면」·「새로 다듬은 판」으로 쓴다(검사가 강제한다).
 *
 * ## 🔴 「모름」은 「괜찮음」이 아니다
 * 서버를 못 읽었으면 「전부 괜찮아요」를 띄우지 않는다 — 운영자가 다 됐다고 믿고 나간다.
 */

/** 그림 하나를 어떻게 받았나. */
interface Probe {
  state: ServedState;
  kb: number | null;
}

/** 절대 주소로 — 저장소 그림(`/art/…`)은 웹, 올린 그림(`/assets/…`)은 엣지가 준다. */
function absUrl(url: string): string {
  if (/^https?:/i.test(url)) return url;
  const base = url.startsWith("/assets/") ? adminApi() : contentBase();
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * 두 번 물어본다 — 순서는 shared `servedState` 주석 그대로(다른 열쇠로 원본 먼저, 그다음 진짜 주소).
 *
 * 🔴 **`Range` 머리를 붙이지 않는다** (2026-09-13 운영 실측). 관리자와 웹은 출처가 달라, `Range` 는 브라우저에 따라
 *    사전 확인(preflight)을 부른다. 운영 서버는 그 확인에 `/art/` 405 · `/assets/` 404 로 답해 **그림이 전부
 *    「못 물어봤어요」**가 된다. 평범한 GET 은 사전 확인이 없다 — 머리만 받고 **몸통은 끊는다**(그림 수십 장을 안 받는다).
 *    무게는 `Content-Length` 에서(shared `sizeKbFromHeaders`).
 */
async function probe(url: string, accept?: readonly string[]): Promise<Probe> {
  const full = absUrl(url);
  const bust = `${full}${full.includes("?") ? "&" : "?"}cb=${Math.random().toString(36).slice(2)}`;
  const ask = async (u: string) => {
    const ctl = new AbortController();
    try {
      const r = await fetch(u, { cache: "no-store", signal: ctl.signal });
      const got = { type: r.headers.get("content-type") ?? "", range: r.headers.get("content-range"), length: r.headers.get("content-length") };
      ctl.abort(); // 머리만 — 몸통(그림)은 안 받는다. 몸통을 안 읽으니 끊어도 거부가 새지 않는다
      return got;
    } catch {
      return null; // 못 물어봤다 — 「없다」가 아니다(servedState 가 unknown 으로 가른다)
    }
  };
  const origin = await ask(bust);
  const kb = sizeKbFromHeaders(origin?.range ?? null, origin?.length ?? null);
  const first = servedState(origin?.type ?? null, null, accept);
  if (first !== "unknown") return { state: first, kb };
  if (origin === null) return { state: "unknown", kb: null };
  const real = await ask(full);
  return { state: servedState(origin.type, real?.type ?? null, accept), kb };
}

const PUBLISH_TEXT: Record<PublishState, { icon: string; text: string; tone: "ok" | "warn" | "err" }> = {
  live: { icon: "✅", text: "아이 화면에 있어요", tone: "ok" },
  differs: { icon: "🟡", text: "새로 다듬은 판과 달라요", tone: "warn" },
  missing: { icon: "⛔", text: "아직 안 올렸어요 — 눌러도 아무 일이 안 나요", tone: "err" },
};

const CAMERA_TEXT: Record<CameraState, { icon: string; text: string; tone: "ok" | "warn" | "err" | "info" }> = {
  on: { icon: "📷", text: "카메라로도 돼요", tone: "ok" },
  off: { icon: "📖", text: "책에서만 돼요(카메라 없음)", tone: "info" },
  unrecognizable: { icon: "📖", text: "카메라가 못 알아보는 쪽이라 책에서만 돼요", tone: "info" },
  blocked: { icon: "⛔", text: "카메라가 못 알아보는 쪽인데 카메라가 켜져 있어요 — [전자책]에서 카메라를 꺼 주세요", tone: "err" },
};

export function MagicHealthPanel({ onGoBook, onGoCard }: { onGoBook: () => void; onGoCard?: () => void }) {
  const [repo, setRepo] = useState<HealthAssets | null>(null);
  const [live, setLive] = useState<HealthAssets | null>(null);
  const [failed, setFailed] = useState(false);
  const [where, setWhere] = useState<Record<string, string>>({});
  const [probes, setProbes] = useState<Record<string, Probe>>({});
  const [onlyTodo, setOnlyTodo] = useState(true);
  const [round, setRound] = useState(0);

  const load = useCallback(async () => {
    setFailed(false);
    setProbes({});
    const get = async (url: string): Promise<HealthAssets | null> => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) return null;
        const m = (await r.json()) as { assets?: HealthAssets };
        return m?.assets ?? null;
      } catch {
        return null;
      }
    };
    const [rp, lv] = await Promise.all([get(`${contentBase()}/content/asset-manifest.json`), get(`${adminApi()}/api/asset-manifest`)]);
    if (!rp || !lv) {
      setRepo(null);
      setLive(null);
      setFailed(true);
      return;
    }
    setRepo(rp);
    setLive(lv);
    setRound((n) => n + 1);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const health = useMemo(() => (repo && live ? magicHealth(repo, live) : null), [repo, live]);

  /** 쪽 이름을 사람 말로 — 쪽 id 와 쪽 번호는 한 칸 어긋난다(표지가 1쪽). */
  useEffect(() => {
    if (!health) return;
    let alive = true;
    // 🃏 경주 카드는 카드 제목으로 — 책 문서가 아니라 카드 목록(`ar-scenes.json`)에 있다
    if (health.rows.some((r) => r.kind === "card")) {
      void fetch(`${contentBase()}/content/ar-scenes.json`, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((raw: unknown) => {
          if (!alive || !raw) return;
          const list = Array.isArray(raw) ? raw : ((raw as { scenes?: unknown[] }).scenes ?? []);
          const next: Record<string, string> = {};
          for (const it of list as { id?: unknown; title_ko?: unknown }[]) {
            if (typeof it.id === "string" && typeof it.title_ko === "string") next[it.id] = `🃏 경주 카드 「${it.title_ko}」`;
          }
          setWhere((w) => ({ ...w, ...next }));
        })
        .catch(() => undefined); // 카드 제목을 못 읽으면 장면 이름으로 보인다 — 점검 자체는 그대로
    }
    for (const b of new Set(health.rows.filter((r) => r.kind === "page").map((r) => r.bookId))) {
      void loadBookDoc(b)
        .then((doc) => {
          if (!alive) return;
          const title = typeof doc.title_ko === "string" ? doc.title_ko : b;
          const next: Record<string, string> = {};
          doc.pages.forEach((p, i) => {
            const pid = String((p as { id?: unknown }).id ?? "");
            if (pid) next[`${b}.${pid}`] = `《${title}》 ${i + 1}쪽`;
          });
          setWhere((w) => ({ ...w, ...next }));
        })
        .catch(() => undefined); // 쪽 번호를 못 읽으면 장면 이름으로 보인다 — 점검 자체는 그대로
    }
    return () => {
      alive = false;
    };
  }, [health]);

  /** 그림·3D 가 정말 오나 — 네 개씩 나눠 묻는다(교실 회선에서 수십 장을 한꺼번에 찌르지 않게). */
  useEffect(() => {
    if (!health) return;
    let alive = true;
    const jobs: { url: string; accept?: readonly string[] }[] = [];
    const seen = new Set<string>();
    const add = (url: string, accept?: readonly string[]) => {
      if (seen.has(url)) return; // 여러 쪽이 같은 그림을 쓴다(수막새 3D 는 세 쪽) — 한 번만 묻는다
      seen.add(url);
      jobs.push({ url, accept });
    };
    for (const r of health.rows) {
      for (const i of r.images) add(i.url);
      for (const m of r.models) add(m.url, MODEL_ACCEPT);
    }
    let next = 0;
    const worker = async () => {
      while (alive && next < jobs.length) {
        const job = jobs[next++]!;
        const got = await probe(job.url, job.accept);
        if (alive) setProbes((p) => ({ ...p, [job.url]: got }));
      }
    };
    void Promise.all([worker(), worker(), worker(), worker()]);
    return () => {
      alive = false;
    };
    // 🔴 다시 확인할 때마다(round) 새로 묻는다 — health 객체는 같은 판이면 그대로다
  }, [health, round]);

  const rowTodo = (r: MagicHealthRow) => {
    const files = [...r.images, ...r.models].map((f) => probes[f.url]);
    const broken = files.filter((p) => p && p.state !== "ok" && p.state !== "unknown").length;
    return r.publish !== "live" || r.camera === "blocked" || broken > 0;
  };

  return (
    <div className="mhealth">
      <PageHead
        icon="🪄"
        title="마법 점검"
        desc="책마다 「누르면 일어나는 마법」이 아이 화면에 잘 나가는지 봐요. 안 올린 마법은 바로 아래에서 올릴 수 있어요."
      />

      <MagicPublishCard />

      <div className="mhealth__bar">
        <button type="button" className="btn btn--sm" onClick={() => void load()}>
          🔄 다시 확인
        </button>
        {health ? (
          <label className="mhealth__only">
            <input type="checkbox" checked={onlyTodo} onChange={(e) => setOnlyTodo(e.target.checked)} /> 손쓸 곳만 보기
          </label>
        ) : null}
      </div>

      {failed ? (
        <p className="mhealth__note mhealth__note--warn" role="status">
          ⚠️ 아이 화면이 지금 무엇을 받는지 <b>못 물어봤어요</b>(서버 연결). 잠시 뒤 [🔄 다시 확인]을 눌러 주세요 —
          이 상태에서는 괜찮은지 알 수 없어요.
        </p>
      ) : !health ? (
        <p className="hint">확인하는 중…</p>
      ) : (
        <>
          <p className="mhealth__sum">
            마법이 있는 쪽 <b>{health.rows.length}곳</b> · 손쓸 곳{" "}
            <b className={health.rows.filter(rowTodo).length ? "mhealth__bad" : ""}>{health.rows.filter(rowTodo).length}곳</b>
          </p>
          {health.rows.filter(rowTodo).length === 0 && onlyTodo ? (
            <p className="mhealth__note mhealth__note--ok">✅ 손쓸 곳이 없어요. [손쓸 곳만 보기]를 끄면 전부 볼 수 있어요.</p>
          ) : null}
          <ul className="mhealth__list">
            {health.rows
              .filter((r) => !onlyTodo || rowTodo(r))
              .map((r) => {
                const pub = PUBLISH_TEXT[r.publish];
                const cam = CAMERA_TEXT[r.camera];
                const files = [...r.images, ...r.models];
                const got = files.map((f) => probes[f.url]).filter((p): p is Probe => !!p);
                const notDeployed = got.filter((p) => p.state === "not_deployed").length;
                const rotten = got.filter((p) => p.state === "rotten").length;
                const unknown = got.filter((p) => p.state === "unknown").length;
                const heavy = got.filter((p) => (p.kb ?? 0) > MAGIC_HEAVY_KB);
                const waiting = files.length - got.length;
                return (
                  <li key={r.sceneId} className="mhealth__row" data-scene={r.sceneId}>
                    <div className="mhealth__name">
                      <b>{where[r.sceneId] ?? r.sceneId}</b>
                      <span className="hint">
                        {" "}
                        · 누르는 자리 {r.hotspots}곳{r.models.length ? " · 🧊 3D" : ""}
                      </span>
                    </div>
                    <div className="mhealth__chips">
                      <span className={`mhealth__chip mhealth__chip--${pub.tone}`}>
                        {pub.icon} {pub.text}
                      </span>
                      <span className={`mhealth__chip mhealth__chip--${cam.tone}`}>
                        {cam.icon} {cam.text}
                      </span>
                      {files.length === 0 ? null : waiting > 0 ? (
                        <span className="mhealth__chip mhealth__chip--info">⏳ 그림 확인 중</span>
                      ) : notDeployed ? (
                        <span className="mhealth__chip mhealth__chip--err">
                          ⛔ 그림 {notDeployed}장이 아직 안 내려갔어요 — 개발자에게 배포를 부탁해 주세요
                        </span>
                      ) : rotten ? (
                        <span className="mhealth__chip mhealth__chip--warn">🟡 그림 {rotten}장이 잠시 옛것으로 보여요 — 몇 시간 뒤 저절로 풀려요</span>
                      ) : unknown ? (
                        <span className="mhealth__chip mhealth__chip--info">⚪ 그림 {unknown}장은 못 물어봤어요</span>
                      ) : (
                        <span className="mhealth__chip mhealth__chip--ok">🖼️ 그림 {files.length}장 잘 와요</span>
                      )}
                      {heavy.length ? (
                        <span className="mhealth__chip mhealth__chip--warn">
                          🐘 무거운 그림 {heavy.length}장({Math.round(Math.max(...heavy.map((h) => h.kb ?? 0)))}KB) — 교실 회선에서 몇 초 빈 칸
                        </span>
                      ) : null}
                    </div>
                    {r.camera === "blocked" || r.publish !== "live" ? (
                      <div className="mhealth__act">
                        {r.camera === "blocked" ? (
                          r.kind === "card" ? (
                            <button type="button" className="btn btn--sm" onClick={onGoCard ?? onGoBook}>
                              📷 AR 카드에서 이 카드 카메라 끄기
                            </button>
                          ) : (
                            <button type="button" className="btn btn--sm" onClick={onGoBook}>
                              📖 전자책에서 이 쪽 카메라 끄기
                            </button>
                          )
                        ) : null}
                        {r.publish !== "live" ? <span className="hint">↑ 위 카드에서 [아이 화면에 올리기]</span> : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
          </ul>
        </>
      )}
    </div>
  );
}
